import {
    IVanillaReaderNavigationItem,
    IVanillaReaderNavigationTreeData,
    IVanillaReaderReadingProgressionTimelineData,
    IVanillaReaderSyntheticPageData, IVanillaReaderSyntheticPageListData,
    IVanillaReaderTimelineProgressData,
    IVanillaReaderVisibleRangeData,
    VanillaProgressionSliderCallback,
    VanillaReaderNavigationType,
    VanillaReaderPaginationType
} from '../VanillaReader/VanillaReaderModel';
import {VanillaUiNavigationIntentEventCallback} from "./VanillaReaderUiModel";
import {IntRange} from "../utils/IntRange";
import {
    ContentPositionTimelineUnit,
    NavigationCollectionType
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";

/**
 * # VanillaReaderUiSliderProgression
 *
 * ## RESPONSIBILITIES
 *
 * ## RELATED TYPES
 *
 */
export class VanillaReaderUiSliderProgression {
    private _sliderProgressionElement: HTMLElement;
    private _sliderProgressionInputElement: HTMLInputElement;
    private _sliderProgressionLabelElement: HTMLElement;
    private _buttonPrevious: HTMLElement;
    private _buttonNext: HTMLElement;
    private _hostDocument: Document;

    private _visibleRange: IntRange | undefined;
    private _activeSyntheticPage: IVanillaReaderSyntheticPageData | undefined;
    private _timelineLength: number = 0;
    private _sliderProgressionChangeTimeout: number | undefined;
    private _showSyntheticPageNumbers: boolean = true;
    private _syntheticPages: IVanillaReaderSyntheticPageData[] = [];
    private _sliderStepSize: number = 1;

    private _navigateNextCallback: VanillaUiNavigationIntentEventCallback | undefined;
    private _navigatePreviousCallback: VanillaUiNavigationIntentEventCallback | undefined;
    private _navigateToTimelinePositionCallback: VanillaUiNavigationIntentEventCallback | undefined;
    private _progressSliderUpdatedCallback: VanillaProgressionSliderCallback | undefined = undefined;

    private _style = `
        <style>                        
            #vanilla-reader__slider-progression {
                display: flex;
                flex-grow: 1;
                flex-direction: row;
                flex-wrap: wrap;            
                width: 100%; 
            }
                        
            #vanilla-reader__slider-progression button {
                font-size: x-large;
                align-content: center;
                min-width: 48px;    
                margin: 8px 0px;
                opacity: 1;
                height: 24px;
                width: auto;            
                bottom: 8px;                    
                transition: opacity, border ease-in-out 0.2s;            
                cursor: pointer;
                z-index: 1;
                background-color: transparent;
                border: 1px solid transparent;
                font-size: 2.5rem;
            }

            #vanilla-reader__slider-progression__input {
                flex-grow: 8;
                padding: 0;
            }
            
            #vanilla-reader__slider-progression__label {
                height: 0px;
                margin: 0;
                width: 100%;
                justify-self: stretch;
                text-align: center;
                font-size: 0.8em;
                line-height: 1.5;
                color: var(--vanilla-reader__color__ui__foreground-dark);
            }


            input[type="range"] {
                -webkit-appearance: none;
                outline:none;
                appearance: none;
                background: transparent;
                cursor: pointer;
                border: none;
                border-radius: 16px;
                height: 2px;
                background: hsla(0,0%,100%,.2);
            }

            #vanilla-reader__slider-progression.loading input[type="range"]{
                background: linear-gradient(90deg, hsla(0,0%,100%,0.2), var(--vanilla-reader__color__ui__accent) 10%, hsla(0,0%,100%,0.2));
                background-size: 200% auto;
                background-position: 0 100%;
                animation: loadingRangeSliderAnimation 1s infinite;
                animation-fill-mode: forwards;
                animation-timing-function: linear;
            }

            @keyframes loadingRangeSliderAnimation {
                0%   { background-position: 0 0; }
                100% { background-position: -200% 0; }
            }

             /* Thumb: webkit */
            input[type="range"]::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                height: 15px;
                width: 15px;
                background-color: var(--vanilla-reader__color__ui__accent);
                border-radius: 50%;
                border: none;
                transition: .2s ease-in-out;
            }
            #vanilla-reader__slider-progression.loading input[type="range"]::-webkit-slider-thumb {
                visibility: hidden;
            }

            /* Thumb: Firefox */
            input[type="range"]::-moz-range-thumb {
                height: 15px;
                width: 15px;
                background-color: var(--vanilla-reader__color__ui__accent);
                border-radius: 50%;
                border: none;
                transition: .2s ease-in-out;/\
            }
            #vanilla-reader__slider-progression.loading input[type="range"]::-moz-range-thumb {
                visibility: hidden;
            }
             
            /* Removes default focus */
            input[type="range"]:focus {
               outline: none;
            }
             
            input[type="range"]::-webkit-slider-thumb:hover {
                box-shadow: 0 0 0 10px rgba(54, 214, 119, .1)
            }
            input[type="range"]:active::-webkit-slider-thumb {
                box-shadow: 0 0 0 13px rgba(54, 214, 119, .2)
            }
            input[type="range"]:focus::-webkit-slider-thumb {
                box-shadow: 0 0 0 13px rgba(54, 214, 119, .2)
            }
            
            /* Hover, active & focus Thumb: Firfox */
            
            input[type="range"]::-moz-range-thumb:hover {
                box-shadow: 0 0 0 10px rgba(54, 214, 119, .1)
            }
            input[type="range"]:active::-moz-range-thumb {
                box-shadow: 0 0 0 13px rgba(54, 214, 119, .2)
            }
            input[type="range"]:focus::-moz-range-thumb {
                box-shadow: 0 0 0 13px rgba(54, 214, 119, .2)   
            }
            

        </style>`;

    private _template = `
            <div id="vanilla-reader__slider-progression" class="loading">
                <label id="vanilla-reader__slider-progression__label" aria-hidden="true" for="vanilla-reader__slider-progression__input"></label>
                <div style="display: flex; width: 100%; justify-content: space-between; align-items: center;">
                    <button id="vanilla-reader__progression-slider__button-previous" title="Go to previous"><span aria-hidden="true">navigate_before</span></button> 
                    <input type="range" id="vanilla-reader__slider-progression__input" title="Reading progression slider" value="0">
                    <button id="vanilla-reader__progression-slider__button-next" title="Go to next"><span aria-hidden="true">navigate_next</span></button>
                </div>
             </div>`;

    constructor(
        private _hostElement: HTMLElement,
        insertPosition: InsertPosition = 'beforeend',
        private _timelineData?: IVanillaReaderReadingProgressionTimelineData,
        private _navigationTreeData?: IVanillaReaderNavigationTreeData,
    ) {
        this._hostDocument = _hostElement.ownerDocument!;
        this._hostElement.insertAdjacentHTML(insertPosition, this._template);
        this._hostElement.insertAdjacentHTML('afterbegin', this._style);

        this._sliderProgressionElement = this._hostDocument.getElementById('vanilla-reader__slider-progression') as HTMLInputElement;
        this._sliderProgressionInputElement = this._sliderProgressionElement.querySelector('#vanilla-reader__slider-progression__input') as HTMLInputElement;
        this._sliderProgressionLabelElement = this._sliderProgressionElement.querySelector('#vanilla-reader__slider-progression__label')! as HTMLElement;

        this._buttonPrevious = this._hostDocument.getElementById('vanilla-reader__progression-slider__button-previous')!;
        this._buttonNext = this._hostDocument.getElementById('vanilla-reader__progression-slider__button-next')!;

        this._buttonPrevious.addEventListener('click', (_ev) => {
            if (this._navigatePreviousCallback) {
                this._navigatePreviousCallback({navigationType: VanillaReaderNavigationType.PREVIOUS});
            }
        });

        this._buttonNext.addEventListener('click', (_ev) => {
            if (this._navigateNextCallback) {
                this._navigateNextCallback({navigationType: VanillaReaderNavigationType.NEXT});
            }
        });

        if(this._timelineData) {
            this.initializeTimeline(this._timelineData, this._navigationTreeData);
        }
    }

    public get value(): number {
        return parseInt(this._sliderProgressionInputElement.value);
    }

    public get disabled(): boolean {
        return this._sliderProgressionInputElement.disabled;
    }

    public set disabled(value: boolean) {
        this._sliderProgressionInputElement.disabled = value;
    }

    public get showSyntheticPageNumbers() :boolean {
        return this._showSyntheticPageNumbers;
    }

    public set showSyntheticPageNumbers(value: boolean) {
        this._showSyntheticPageNumbers = value;
    }

    public get syntheticPageCount(): number {
        let returnValue: number;

        switch (this._timelineData?.unit) {
            case ContentPositionTimelineUnit.PAGES:
                returnValue = this._timelineData.sections?.length || 0;
                break;
            case ContentPositionTimelineUnit.DOCUMENTS:
                returnValue = this._timelineData.sections?.length || 0;
                break;
            default:
                returnValue = this._syntheticPages.length;
        }
        return returnValue;
    }

    public get visibleRange(): IntRange | undefined{
        return this._visibleRange;
    }

    public get syntheticPages(): IVanillaReaderSyntheticPageData[] {
        return this._syntheticPages;
    }

    setSyntheticPages(pageList: IVanillaReaderSyntheticPageListData): void {
        this._syntheticPages = pageList.pages;
        if(this._timelineData) {
            this.initializeTimeline(this._timelineData, this._navigationTreeData);
            this._sliderStepSize = Math.floor(this._timelineLength / this.syntheticPageCount);
            this._setSliderElementAttributes(0,this._timelineLength - 1,this.value, this._sliderStepSize);

            this.updateSliderProgressionValue(this.value);
        }
    }

    public get visibleSyntheticPages(): IVanillaReaderSyntheticPageData[] | undefined {
        const visibleRange = this._visibleRange;

        if(!visibleRange) {
            console.warn('VanillaReaderUiSliderProgression.visibleSyntheticPages, No visible range has been set.');
            return;
        }
        return this._syntheticPages.filter(page => {
            return page.range.intersects(visibleRange);
        });
    }

    public get activeSyntheticPage(): IVanillaReaderSyntheticPageData | undefined {
        return this._activeSyntheticPage;
    }

    public get paginationType(): VanillaReaderPaginationType {
        let returnValue: VanillaReaderPaginationType;

        let pageList = this._navigationTreeData?.collections.find(collection => collection.type === NavigationCollectionType.PAGE_LIST);

        switch (this._timelineData?.unit) {
            case ContentPositionTimelineUnit.PAGES:
                returnValue = VanillaReaderPaginationType.SECTIONS;
                break;
            case ContentPositionTimelineUnit.DOCUMENTS:
                returnValue = VanillaReaderPaginationType.SECTIONS;
                break;
            default:
                returnValue = pageList ? VanillaReaderPaginationType.PAGE_LIST : VanillaReaderPaginationType.SYNTHETIC;
        }
        return returnValue;

    }

    onNavigatePrevious(callback: VanillaUiNavigationIntentEventCallback): void {
        this._navigatePreviousCallback = callback;
    }

    onNavigateNext(callback: VanillaUiNavigationIntentEventCallback): void {
        this._navigateNextCallback = callback;
    }

    onNavigateToTimelinePosition(callback: VanillaUiNavigationIntentEventCallback): void {
        this._navigateToTimelinePositionCallback = callback;
    }

    onProgressionSliderUpdated(callback: VanillaProgressionSliderCallback): void {
        this._progressSliderUpdatedCallback = callback;
    }


    initializeTimeline(timelineData: IVanillaReaderReadingProgressionTimelineData, navigationTreeData?: IVanillaReaderNavigationTreeData): void {

        if(navigationTreeData) {
            this._navigationTreeData = navigationTreeData;
        }

        this._timelineLength = timelineData.length;
        this._timelineData = timelineData;

        let syntheticPageStepSize = this.syntheticPageCount;
        this._sliderStepSize = Math.floor(syntheticPageStepSize <= this._timelineLength ? syntheticPageStepSize : 1);

        this._sliderProgressionInputElement.disabled = false;

        this._setSliderElementAttributes(0, this._timelineLength - 1, 0, this._sliderStepSize);

        this._sliderProgressionInputElement.addEventListener('change', this._event_menu_sliderProgressionChange);
        this._sliderProgressionInputElement.addEventListener('input', this._event_sliderProgressionInput);

    }

    updateSliderProgressionValue(value: number): void {

        let timelineProgress = parseFloat((value / this._timelineLength).toFixed(2));

        this._activeSyntheticPage = this.getSyntheticPageDataFromPosition(value);
        let syntheticPageRange: IntRange | undefined = this._activeSyntheticPage?.range;
        let pageLength = syntheticPageRange ? syntheticPageRange.end - syntheticPageRange.start : 0;
        let positionOffset = syntheticPageRange ? value - syntheticPageRange.start : 0;
        let syntheticPageProgress =  syntheticPageRange ? (positionOffset / pageLength % 1.0) * 100 : 0;

        if(syntheticPageRange && this.visibleRange && (this.visibleRange.end >= syntheticPageRange.end || this.visibleRange.start === this.visibleRange.end)) {
            syntheticPageProgress = 100;
        }

        syntheticPageProgress = this.syntheticPageCount <= this._timelineLength ? syntheticPageProgress : 100;

        let progressData: IVanillaReaderTimelineProgressData = {
            pageData: this._activeSyntheticPage,
            pageProgress: syntheticPageProgress,
            timelineProgress,
            timelineValue: value
        }

        this.updateSliderBackground(progressData);
        //this.updateProgressionLabelValue(progressData);
        this._sliderProgressionInputElement.value = value.toString();

        if(this._progressSliderUpdatedCallback) {
            this._progressSliderUpdatedCallback(value, progressData)
        }
    }

    updateSliderBackground(progressData: IVanillaReaderTimelineProgressData): void {
        const tempSliderValue = progressData.timelineValue;          
        const progress = (tempSliderValue / parseInt(this._sliderProgressionInputElement.max)) * 100;
        this._sliderProgressionInputElement.style.background = `linear-gradient(to right, var(--vanilla-reader__color__ui__accent) ${progress}%, hsla(0,0%,100%,.2) ${progress}%)`;
    }

    updateProgressionLabelValue(progressData: IVanillaReaderTimelineProgressData): void {
        let navItemtitle = this._getNearestNavItemFromNavCollection(progressData.timelineValue, NavigationCollectionType.TOC)?.title;
        navItemtitle = navItemtitle ? navItemtitle : '';
        let progressionLabelText: string;

        if(this.showSyntheticPageNumbers) {
            let positionText = progressData.pageData?.label ? `location ${progressData.pageData.label} of ${this._syntheticPages[this._syntheticPages.length - 1].label}` : '';
            progressionLabelText = `${navItemtitle} \n ${positionText}`;
        } else {
            progressionLabelText = `${navItemtitle} (${Math.floor(progressData.timelineProgress * 100)}%)`;
        }

        this._sliderProgressionLabelElement.innerText = progressionLabelText;

    }

    setNavigationTreeData(treeData: IVanillaReaderNavigationTreeData): void {
        this._navigationTreeData = treeData;
    }

    setVisibleRangeData(rangeData: IVanillaReaderVisibleRangeData): void {
        if(rangeData.timelineRange) {
            this._visibleRange = new IntRange(rangeData.timelineRange.start, rangeData.timelineRange.end);
        }
    }

    getSyntheticPageDataFromPosition(position: number): IVanillaReaderSyntheticPageData | undefined {
        return  this._syntheticPages.find((page)=>{
            return (position >= page.range.start && position < page.range.end) || (position === page.range.start && position === page.range.end);
        });
    }

    /*
    *
    * PRIVATE METHODS
    *
    * */

    private _setSliderElementAttributes(min: number = 0, max: number, value: number, step: number = 1): void {
        this._sliderProgressionInputElement.setAttribute('min', min.toString());
        this._sliderProgressionInputElement.setAttribute('max', max.toString());
        this._sliderProgressionInputElement.setAttribute('value', value.toString());
        this._sliderProgressionInputElement.setAttribute('step', step.toString());
    }

    private _getNearestNavItemFromNavCollection(position: number, collectionType: NavigationCollectionType): IVanillaReaderNavigationItem | undefined  {
        if (this._navigationTreeData) {
            let tree = this._navigationTreeData;

            if (!tree) {
                console.warn('VanillaReaderUiSliderProgression._getNearestNavItemFromNavCollection(), No navigation tree data.')
                return undefined;
            }

            let collection = tree.collections.find(collection => collection.type === collectionType);

            if(!collection) {
                console.warn('VanillaReaderUiSliderProgression._getNearestNavItemFromNavCollection(), Publication has no TOC.')
                return undefined;
            }

            let currentPos = 0;
            let foundItem: IVanillaReaderNavigationItem | undefined = undefined;

            while (currentPos < collection.children.length) {
                let currentItem = collection.children[currentPos];

                if(position === currentItem.timelineStartPosition) {
                    foundItem = currentItem;
                    break;
                }

                if (currentItem.timelineStartPosition === undefined || position > currentItem.timelineStartPosition) {
                    currentPos++;
                } else {
                    // If the position is greater than the current items position we return the previous nav item,
                    // otherwise we return the current position.
                    let itemIndex = currentItem.timelineStartPosition > position ? currentPos - 1 : currentPos;
                    foundItem = collection.children[itemIndex];
                    break;
                }
            }
            return foundItem;
        }
        return undefined;
    }

    private _event_sliderProgressionInput = (_event: Event) => {
        let sliderValue = parseInt(this._sliderProgressionInputElement.value);
        if (!isNaN(sliderValue)) {
            this.updateSliderProgressionValue(sliderValue);
        }
    };

    private _event_menu_sliderProgressionChange = (_event: Event) => {

        let sliderPosition = parseInt(this._sliderProgressionInputElement.value);

        if (this._sliderProgressionChangeTimeout) {
            window.clearTimeout(this._sliderProgressionChangeTimeout);
            this._sliderProgressionChangeTimeout = undefined;
        }

        this._sliderProgressionChangeTimeout = window.setTimeout(() => {

            if (this._navigateToTimelinePositionCallback) {
                this._navigateToTimelinePositionCallback({
                    navigationType: VanillaReaderNavigationType.TIMELINEPOSITION,
                    position: sliderPosition,
                });
            }

        }, 250);

        this.updateSliderProgressionValue(sliderPosition);
    };

}
